import { IPerson } from '@shared/models/person';
import { ICompany } from '@shared/models/company';
import {
    ProductSubscription,
    ProductSubscriptionDto,
    ProductSubscriptionType
} from '@shared/models/product-subscription';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { BookingDialogService } from '@sharedComponents/dialogs/booking-dialog/services/booking-dialog.service';
import { BookingDialogBaseEntity } from '@sharedComponents/dialogs/booking-dialog/services/classes/abstract/booking-dialog-base-entity';
import { IProduct, Product } from '@shared/models/product';
import { BcmCostCenterDto } from '@shared/models/bcm-cost-center';
import { BcmDynamicPrice } from '@shared/models/bcm-dynamic-price';
import { take } from 'rxjs';
import { BookingAttribute } from '@sharedComponents/dialogs/booking-dialog/enums/booking-attribute.enum';
import { hasUnmatchedPriceRules } from '@shared/functions/resolve-dynamic-price-state';

export class BookingDialogSubscriptions extends BookingDialogBaseEntity<ProductSubscription, ProductSubscription[]> {

    constructor(bookingDialogService: BookingDialogService) {
        super(bookingDialogService, BookingAttribute.SUBSCRIPTIONS);
    }

    /** Produkte, deren Abo wegen einer nicht greifenden Preisregel unterblieben ist. (#7017) */
    private readonly _unmatchedProductIds = new Set<number>();

    protected isDuplicate(value: ProductSubscription): boolean {
        return this.bookingDialogService.subscriptions.value.findIndex(sub => {
            return sub.product.id === value.product.id &&
                sub.quantity === value.quantity &&
                sub.type === ProductSubscriptionType.TenantRelation &&
                !sub.id;
        }) !== -1;
    }

    addFromTenantRelation(assignment: TenantRelationAssignment, products?: Product[]): void {

        const relationProducts = products ?? assignment?.tenantRelation?.products;

        if (!relationProducts?.length) {
            return;
        }

        const subscriptions = relationProducts.map(product => {

            const subscriptionDto: ProductSubscriptionDto = {
                product: product as unknown as IProduct,
                payableOption: assignment.payableOption,
                quantity: product.quantity,
                type: ProductSubscriptionType.TenantRelation,
                vestingPeriodFrom: assignment.fromDate,
                vestingPeriodUntil: assignment.toDate,
                fromDate: assignment.fromDate,
                toDate: assignment.toDate,
                lastDayOfMonth: assignment.lastDayOfMonth,
                tenantRelationName: assignment.tenantRelation.name,
                costCenter: assignment.costCenter as unknown as BcmCostCenterDto,
                person: this.bookingDialogService.person.value as unknown as IPerson,
                company: this.bookingDialogService.company.value as unknown as ICompany,
            };

            return new ProductSubscription(subscriptionDto);

        });

        subscriptions.forEach(subscription => {
            this.add(subscription);
        });

        this._applyDynamicPrices(assignment, relationProducts);

    }

    /**
     * Zieht die Abos auf eine neue Auswertung nach, wie es resyncTenantRelationPrices() für die
     * Positionen tut: die Regeln hängen am Boot, das im Kartenfall erst nach der Beziehung gewählt
     * wird. Fällt ein Produkt jetzt durch seine Regeln, verschwindet sein Abo; greifen sie wieder,
     * kommt es zurück. (#7017)
     *
     * Arbeitet auf der übergebenen Auswertung, statt eine eigene anzustoßen: eine zweite, nicht
     * abgebrochene Anfrage je Kontextwechsel würde mit der ersten um die Antwort rennen, und es
     * gewänne die zuletzt *eingetroffene* statt der zuletzt *gestellten*.
     */
    resyncTenantRelationPrices(
        assignment: TenantRelationAssignment,
        dynamicPrices: Map<number, BcmDynamicPrice>,
    ): void {

        const products = assignment?.tenantRelation?.products;

        if (this.bookingDialogService.readonlyView || assignment?.toDate || !products?.length) {
            return;
        }

        this._applyEvaluatedPrices(products, dynamicPrices);

        const covered = new Set(this.value
            .filter(subscription => subscription.type === ProductSubscriptionType.TenantRelation)
            .map(subscription => subscription.product?.id));

        const restorable = products.filter(product =>
            !covered.has(product.id)
            && this._unmatchedProductIds.has(product.id)
            && dynamicPrices.has(product.id)
            && !hasUnmatchedPriceRules(product, dynamicPrices.get(product.id)));

        if (restorable.length) {
            this.addFromTenantRelation(assignment, restorable);
        }
    }

    /**
     * Übernimmt den ausgewerteten Regelpreis in die Abos. Läuft bewusst über denselben Evaluator wie
     * die "Produkte gefunden"-Box (BookingDialogPositions.evaluateTenantRelationProductPrices), damit
     * Box und Abo nicht auseinanderlaufen können – ohne Enddatum der Beziehung entstehen Abos statt
     * Positionen, und die zeigten bisher den Grundpreis, im Ticket 0,00 EUR. (#6922)
     *
     * Der Preis liegt als customPrice/customTitle am Abo: bcm-bookings.service schreibt beide beim
     * Insert und Patch, und die Abo-Zeile rendert `customPrice || product.price`.
     */
    private _applyDynamicPrices(assignment: TenantRelationAssignment, products: Product[]): void {

        if (!products.some(product => product.hasDynamicPrice)) {
            return;
        }

        this.bookingDialogService.positions
            .evaluateTenantRelationProductPrices(assignment, products)
            .pipe(take(1))
            .subscribe(dynamicPrices => this._applyEvaluatedPrices(products, dynamicPrices));
    }

    /** Trägt eine fertige Auswertung in die Abos ein – der gemeinsame Kern beider Aufrufer. */
    private _applyEvaluatedPrices(products: Product[], dynamicPrices: Map<number, BcmDynamicPrice>): void {

        let changed = false;

        // Abos, deren Regel nicht greift, gar nicht erst stehen lassen: ohne Treffer liefert der
        // Evaluator den Grundpreis des Produkts, und das Abo sähe damit wie ein normal abzurechnendes
        // aus – dieselbe Verwechslungsgefahr wie bei den Positionen. (#7017)
        const unmatched = this.value.filter(subscription =>
            subscription.type === ProductSubscriptionType.TenantRelation
            && !subscription.id
            && dynamicPrices.has(subscription.product?.id)
            && hasUnmatchedPriceRules(
                products.find(product => product.id === subscription.product?.id),
                dynamicPrices.get(subscription.product.id)));

        if (unmatched.length) {
            unmatched.forEach(subscription => this._unmatchedProductIds.add(subscription.product.id));
            this.value = this.value.filter(subscription => !unmatched.includes(subscription));
            changed = true;
        }

        this.value
            .filter(subscription => subscription.type === ProductSubscriptionType.TenantRelation)
            .forEach(subscription => this._unmatchedProductIds.delete(subscription.product?.id));

        this.value
            .filter(subscription => subscription.type === ProductSubscriptionType.TenantRelation && !subscription.id)
            .forEach(subscription => {
                const dynamicPrice = dynamicPrices.get(subscription.product?.id);

                if (!dynamicPrice) {
                    return;
                }

                subscription.dynamicPrice = dynamicPrice;
                subscription.customPrice = dynamicPrice.rulePrice;
                subscription.customTitle = dynamicPrice.ruleName;
                changed = true;
            });

        if (changed) {
            this.sendUpdate(true);
        }
    }

    removeUnsavedFromTenantRelation(): void {
        // Die Merkliste gehört zur gerade gewählten Beziehung. (#7017)
        this._unmatchedProductIds.clear();

        this.value = this.value
            .filter(subscription => subscription.id || subscription.type !== ProductSubscriptionType.TenantRelation);
    }

    removeFromTenantRelation(): void {
        this.value
            .filter(subscription => subscription.tenantRelationName)
            .forEach(subscription => {
                subscription.deleted = true;
            });
    }

}
